import classNames from 'classnames';
import { parse as parseDate, format as formatDate } from 'date-fns';
import React, { useState, useCallback, useMemo } from 'react';
import DatePicker from 'react-datepicker';

import TextField from './Text';

import styles from '../../../styles/fields/date.module.scss';

import 'react-datepicker/dist/react-datepicker.css';

interface DateFieldProps {
    value?: string;
    type?: string;
    min?: number | string;
    max?: number | string;
    placeholder?: string;
    format?: string;
    inputRef?: React.Ref<HTMLInputElement>;
    onChange?: (value: string) => void;
    onOpen?: () => void;
    onClose?: () => void;
    className?: string;
    placeholderClassName?: string;
    inputClassName?: string;
    [key: string]: any;
}

const DateField: React.FC<DateFieldProps> = ({
    value,
    min,
    max,
    placeholder,
    format = 'yyyy-MM-dd',
    onChange,
    inputRef,
    onOpen = null,
    onClose = null,
    className,
    placeholderClassName,
    inputClassName,
    ...props
}) => {
    const [focused, setFocused] = useState(false);

    const onFocus = useCallback(() => setFocused(true), [setFocused]);
    const onBlur = useCallback(() => setFocused(false), [setFocused]);

    const supportsInputDate = useMemo(() => {
        const input = document.createElement('input');
        input.setAttribute('type', 'date');

        const notADateValue = 'not-a-date';
        input.setAttribute('value', notADateValue);

        return input.value !== notADateValue;
    }, [document]);

    const dateValue = useMemo(
        () => (value !== null ? parseDate(value, format, new Date()) : null),
        [value],
    );
    const minDateValue = useMemo(
        () => (min !== null ? parseDate(min, format, new Date()) : null),
        [min],
    );
    const maxDateValue = useMemo(
        () => (max !== null ? parseDate(max, format, new Date()) : null),
        [max],
    );

    return supportsInputDate ? (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
        >
            <TextField
                {...props}
                className={classNames([
                    styles.input,
                    {
                        [inputClassName]: inputClassName !== null,
                    },
                ])}
                type="date"
                max={max}
                min={min}
                inputRef={inputRef}
                value={value}
                onChange={onChange}
                onFocus={onFocus}
                onBlur={onBlur}
            />
            {!value ? (
                <div
                    className={classNames([
                        styles.placeholder,
                        {
                            [placeholderClassName]: placeholderClassName !== null,
                        },
                    ])}
                >
                    {value || placeholder}
                </div>
            ) : null}
        </div>
    ) : (
        <DatePicker
            customInput={
                <TextField
                    type="text"
                    inputRef={inputRef}
                    placeholder={placeholder}
                    className={classNames([
                        styles.input,
                        {
                            [inputClassName]: inputClassName !== null,
                        },
                    ])}
                    {...props}
                />
            }
            selected={dateValue}
            dateFormat={format}
            minDate={minDateValue}
            maxDate={maxDateValue}
            showYearDropdown
            showMonthDropdown
            dropdownMode="select"
            onInputClick={onOpen}
            onBlur={onClose}
            onSelect={onClose}
            onChange={(val) => onChange(formatDate(val, format))}
            withPortal
        />
    );
};

export default React.forwardRef<HTMLInputElement, Omit<DateFieldProps, 'inputRef'>>(
    (props, ref) => <DateField inputRef={ref} {...props} />,
);
